import { useEffect } from 'react'
import { ChevronLeft, ChevronRight, Download, ExternalLink, FileText, Loader2 } from 'lucide-react'
import type { SupportAttachment } from '@/api/supportApi'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useOpenSupportAttachment, useSupportAttachmentLink } from '@/hooks/useSupport'
import { formatBytes, previewKind } from '@/lib/support/supportFiles'

/**
 * View a ticket's file without downloading it: pictures, PDFs, text, audio
 * and video show here (a short-lived preview link from Smart Support, which
 * decides what may be shown); anything else offers Download. ← / → step
 * through the ticket's other files.
 */
export function AttachmentViewer({
  ticket,
  files,
  index,
  onIndex,
  onClose,
}: {
  ticket: string
  files: SupportAttachment[]
  index: number
  onIndex: (i: number) => void
  onClose: () => void
}) {
  const file = files[index]
  const kind = previewKind(file.mimeType)
  const preview = useSupportAttachmentLink(ticket, file.id, kind !== 'none')
  const hasPrev = index > 0
  const hasNext = index < files.length - 1

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrev) onIndex(index - 1)
      if (e.key === 'ArrowRight' && hasNext) onIndex(index + 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, hasPrev, hasNext, onIndex])

  const url = preview.data?.inline ? preview.data.url : null

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='flex h-[88vh] max-w-5xl flex-col gap-3 p-4'>
        <DialogHeader className='flex-row items-center justify-between gap-3 space-y-0 pr-8'>
          <div className='min-w-0'>
            <DialogTitle className='truncate text-sm'>{file.fileName}</DialogTitle>
            <DialogDescription className='text-xs'>
              {formatBytes(file.sizeBytes)}
              {files.length > 1 ? ` · ${index + 1} of ${files.length}` : ''}
            </DialogDescription>
          </div>
          <div className='flex shrink-0 items-center gap-1'>
            {files.length > 1 ? (
              <>
                <Button variant='ghost' size='sm' disabled={!hasPrev} onClick={() => onIndex(index - 1)} aria-label='Previous file'>
                  <ChevronLeft className='h-4 w-4' />
                </Button>
                <Button variant='ghost' size='sm' disabled={!hasNext} onClick={() => onIndex(index + 1)} aria-label='Next file'>
                  <ChevronRight className='h-4 w-4' />
                </Button>
              </>
            ) : null}
            {url ? (
              <Button variant='ghost' size='sm' onClick={() => window.open(url, '_blank', 'noopener')}>
                <ExternalLink className='h-4 w-4' /> Open
              </Button>
            ) : null}
            <DownloadButton ticket={ticket} attachmentId={file.id} />
          </div>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-lg border border-border bg-muted/40'>
          {kind === 'none' || (preview.data && !preview.data.inline) ? (
            <div className='flex flex-col items-center gap-3 p-8 text-center text-sm text-muted-foreground'>
              <FileText className='h-10 w-10' aria-hidden />
              <p>
                There is no preview for this type of file.
                <br />
                Download it to open it.
              </p>
              <DownloadButton ticket={ticket} attachmentId={file.id} primary />
            </div>
          ) : preview.isLoading ? (
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' aria-label='Loading preview' />
          ) : preview.error ? (
            <p className='p-8 text-sm text-destructive'>{(preview.error as Error).message}</p>
          ) : url && kind === 'image' ? (
            <img src={url} alt={file.fileName} className='max-h-full max-w-full object-contain' />
          ) : url && kind === 'video' ? (
            <video src={url} controls className='max-h-full max-w-full' />
          ) : url && kind === 'audio' ? (
            <audio src={url} controls />
          ) : url ? (
            // PDF and text: the browser's own viewer, sandboxed (no scripts, no top navigation).
            <iframe src={url} title={file.fileName} sandbox='allow-same-origin allow-downloads' className='h-full w-full bg-white' />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** A download link, opened on the click itself (a tab opened after an await is a blocked pop-up). */
function DownloadButton({ ticket, attachmentId, primary }: { ticket: string; attachmentId: string; primary?: boolean }) {
  const open = useOpenSupportAttachment(ticket)
  return (
    <Button variant={primary ? 'default' : 'ghost'} size='sm' onClick={() => open(attachmentId)}>
      <Download className='h-4 w-4' /> Download
    </Button>
  )
}